import './ListPage.scss';
import { SimpleNumberCard, Icon, Checkbox } from '@design-system/index';
import { OrdersListLine, PrinterFull, PrinterLine } from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { OrderTable } from '@services/product/order/order.types';
import { ColumnDef } from '@tanstack/react-table';
import { useGetOrdersMutation } from '@services/product/order/order.endpoints';
import { formatMoney } from '@utils/numbers';
import { useListPageVM } from './useListPageVM';
import { formatDate } from '@utils/dates';
import { OrderStatus } from '@services/product/order/order.types';
import { OrderStatusBadge } from '@components/OrderStatusBadge/OrderStatusBadge';
import { ORDER_STATUS } from '@utils/enumTranslation';
import { useGetOrdersExportMutation } from '@services/product/order/order.endpoints';

export default function ListPage() {
    const { totalOrders, eticketTypes, handleMetadata, columnHelper, navigate, handlePrint } = useListPageVM();

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor('woocommerceId', {
            header: 'Référence',
            enableSorting: true,
            minSize: 160,
            meta: {
                clickable: true
            },
            cell: (info) => <>{'#' + info.getValue()}</>
        }),
        columnHelper.accessor('createdAt', {
            header: 'Date de la commande',
            enableSorting: true,
            minSize: 160,
            meta: {
                clickable: true
            },
            cell: (info) => <>{formatDate(info.getValue() as string)}</>
        }),
        columnHelper.accessor('status', {
            header: 'Statut',
            enableSorting: true,
            minSize: 160,
            meta: {
                clickable: true
            },
            cell: (info) => <OrderStatusBadge status={info.getValue() as OrderStatus} />
        }),
        columnHelper.accessor('etickets', {
            header: 'Type d\'e-billet',
            enableSorting: false,
            meta: {
                clickable: true
            },
            cell: (info) => {
                const eticketTypes = info.getValue().map(eticket => eticket.eticketType);
                const uniqueEticketTypes = new Set<string>();
                eticketTypes.forEach(type => uniqueEticketTypes.add(type.label));

                return <>{Array.from(uniqueEticketTypes).join(', ')}</>;
            }
        }),
        columnHelper.accessor('total', {
            header: 'Total TTC',
            enableSorting: true,
            minSize: 160,
            meta: {
                clickable: true
            },
            cell: (info) => <>{formatMoney(info.getValue() as number)}</>
        }),
        columnHelper.accessor((row) => (row.member ? row.member.cosemNumber : '-'), {
            header: 'N°COSEM',
            id: 'member.cosemNumber',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.accessor(
            (row) =>
                row.member
                    ? row.member.personalInformation.lastname.toUpperCase() + ' ' + row.member.personalInformation.firstname.toUpperCase()
                    : '-',
            {
                header: 'Adhérent',
                id: 'member.personalInformation.lastname',
                enableSorting: true,
                minSize: 160,
                meta: {
                    clickable: true
                },
                cell: (info) => <>{info.getValue()}</>
            }
        ),
        columnHelper.display({
            id: 'identityMismatch',
            header: '',
            size: 0,
            cell: ({ row }) =>
                row.original.hasIdentityMismatch ? (
                    <span title="Facturation ≠ titulaire du compte — commande potentiellement passée par un tiers" role="img" aria-label="Alerte identité">
                        ⚠️
                    </span>
                ) : (
                    <></>
                )
        })
    ] as ColumnDef<OrderTable>[];

    return (
        <div className="orders-table-container">
            <div className="orders-table-container-header">
                <div className="orders-table-container-header-left">
                    <SimpleNumberCard labelForSingle={'Commande'} labelForMultiple={'Commandes'} value={totalOrders} />
                </div>
            </div>
            <MutationTable
                key={'orders'}
                columns={columns}
                mutationHook={useGetOrdersMutation}
                onRowClick={(row) => navigate(`/products-management/orders/${row.id}/details`)}
                defaultSorting={{
                    field: 'woocommerceId',
                    direction: 'desc'
                }}
                selectable={true}
                exportable={true}
                exportMutationHook={useGetOrdersExportMutation}
                headerTitle={'Liste des commandes'}
                headerIcon={<OrdersListLine />}
                onMetadata={handleMetadata}
                filters={[
                    {
                        id: 'createdAt',
                        label: 'Date de la commande',
                        type: 'date',
                        comparatorTypes: ['isBetween', 'isBefore', 'isAfter', 'equals']
                    },
                    {
                        id: 'status',
                        label: 'Statut',
                        type: 'select',
                        options: Object.entries(ORDER_STATUS).map(([state, label]) => ({ label, value: state as OrderStatus }))
                    },
                    {
                        id: 'etickets',
                        label: 'Type d\'e-billet',
                        type: 'select',
                        options: eticketTypes.map((type) => ({ label: type.label, value: String(type.id) }))
                    }
                ]}
                footer={{
                    actions: [
                        {
                            label: 'Imprimer',
                            icon: <Icon icon={<PrinterLine />} size={20} color="primary" />,
                            hoverIcon: <Icon icon={<PrinterFull />} size={20} color="primary" />,
                            onClick: (orders: OrderTable[]) => handlePrint(orders)
                        }
                    ]
                }}
            />
        </div>
    );
}
